import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../utils/api";
import MatchupDetailsModal from "./MatchupDetailsModal";
import MascotLogo from "./MascotLogo";

const SESSION_USER_KEY = "fantasySwimSessionUser";

function getStoredCoachName() {
  try {
    const raw = localStorage.getItem(SESSION_USER_KEY);
    return raw ? JSON.parse(raw).coachName || "" : "";
  } catch {
    return "";
  }
}

const Matchmaker = () => {
  const [state, setState] = useState(null);
  const [mascots, setMascots] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const myCoachName = getStoredCoachName();

  const mascotFor = (coachName) => {
    const key = (coachName || "").toLowerCase();
    return mascots[key]?.image || "";
  };

  const [matchupDetails, setMatchupDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  const openMatchupDetails = async (week, matchupIndex) => {
    setMatchupDetails({ loading: true, week, matchupIndex });
    setDetailsLoading(true);
    setDetailsError("");
    try {
      const res = await axios.get(
        `${API_BASE_URL}/matchups/${week}/${matchupIndex}/details`,
      );
      setMatchupDetails({ ...res.data, loading: false });
    } catch (e) {
      setDetailsError(e.response?.data?.detail ?? e.message);
      setMatchupDetails({ loading: false, error: true, week, matchupIndex });
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeMatchupDetails = () => {
    setMatchupDetails(null);
    setDetailsError("");
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API_BASE_URL}/matchups`),
      axios
        .get(`${API_BASE_URL}/coaches/mascots`)
        .catch(() => ({ data: { mascots: {} } })),
    ])
      .then(([matchupsRes, mascotsRes]) => {
        if (cancelled) return;
        setState(matchupsRes.data);
        setMascots(mascotsRes.data?.mascots || {});
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError("Unable to load matchups.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <div style={stateText}>Loading matchups...</div>;
  if (error) return <div style={errorText}>{error}</div>;

  const matchups = Array.isArray(state?.matchups) ? state.matchups : [];
  const currentWeek = state?.current_week ?? 1;
  const generated = state?.generated ?? false;
  const weekHistory = Array.isArray(state?.week_history) ? state.week_history : [];

  const swissRecords = state?.swiss_records || {};

  const myMatchup = matchups.find(
    (pair) =>
      pair[0]?.coach?.toLowerCase() === myCoachName.toLowerCase() ||
      pair[1]?.coach?.toLowerCase() === myCoachName.toLowerCase(),
  );
  const myOpponent = myMatchup
    ? myMatchup[0]?.coach?.toLowerCase() === myCoachName.toLowerCase()
      ? myMatchup[1]
      : myMatchup[0]
    : null;
  const myRecord = swissRecords[myCoachName] || null;

  return (
    <div style={page}>
      <h2 style={pageTitle}>Matches</h2>
      <p style={subtitle}>Week {currentWeek} — Swiss Bracket</p>

      {!generated ? (
        <p style={emptyHint}>No matchups have been generated yet. Check back soon!</p>
      ) : (
        <>
          {/* ── My Matchup ── */}
          {myMatchup && (
            <div style={myMatchupCard}>
              <div style={myMatchupLabel}>Your Matchup — Week {currentWeek}</div>
              {myRecord && (
                <div style={{ fontSize: "0.78rem", color: "#a8fff0", fontWeight: 700 }}>
                  Your record: {myRecord.wins}-{myRecord.losses} &nbsp;·&nbsp; {Number(myRecord.total_season_points || 0).toFixed(2)} season pts
                </div>
              )}
              <div style={myMatchupRow}>
                <div style={myMatchupCoach}>
                  <MascotLogo
                    imageDataUrl={mascotFor(myCoachName)}
                    coachName={myCoachName}
                    size={220}
                    borderRadius="14px"
                    className="matchup-mascot-lg"
                    style={{ marginBottom: "0.55rem" }}
                  />
                  <span style={myMatchupName}>{myCoachName}</span>
                  <span style={myMatchupPts}>
                    {Number(
                      (myMatchup[0]?.coach?.toLowerCase() === myCoachName.toLowerCase()
                        ? myMatchup[0]
                        : myMatchup[1]
                      )?.total_points || 0,
                    ).toFixed(2)}{" "}
                    season pts
                  </span>
                </div>
                <span style={vsText}>vs</span>
                <div style={myMatchupCoach}>
                  {myOpponent?.isBye ? (
                    <span style={byeText}>BYE</span>
                  ) : (
                    <>
                      <MascotLogo
                        imageDataUrl={mascotFor(myOpponent?.coach)}
                        coachName={myOpponent?.coach}
                        size={220}
                        borderRadius="14px"
                        style={{ marginBottom: "0.55rem" }}
                      />
                      <span style={myMatchupOpponent}>{myOpponent?.coach}</span>
                      <span style={myMatchupPts}>
                        {Number(myOpponent?.total_points || 0).toFixed(2)} season pts
                      </span>
                      {swissRecords[myOpponent?.coach] && (
                        <span style={{ fontSize: "0.72rem", color: "#8ea8d8" }}>
                          {swissRecords[myOpponent.coach].wins}-{swissRecords[myOpponent.coach].losses}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── All matchups ── */}
          <h3 style={sectionTitle}>All Week {currentWeek} Matchups</h3>
          <div style={matchupList}>
            {matchups.map((pair, index) => {
              const isMyMatchup =
                pair[0]?.coach?.toLowerCase() === myCoachName.toLowerCase() ||
                pair[1]?.coach?.toLowerCase() === myCoachName.toLowerCase();
              const rec0 = swissRecords[pair[0]?.coach] || {};
              const rec1 = !pair[1]?.isBye ? (swissRecords[pair[1]?.coach] || {}) : {};
              return (
                <div
                  key={index}
                  style={{ ...matchupCard, ...(isMyMatchup ? matchupCardHighlight : {}) }}
                >
                  <div style={matchupSide}>
                    <MascotLogo
                      imageDataUrl={mascotFor(pair[0]?.coach)}
                      coachName={pair[0]?.coach}
                      size={144}
                      borderRadius="12px"
                      className="matchup-mascot-sm"
                      style={{ marginBottom: "0.45rem" }}
                    />
                    <span style={matchupCoachName}>{pair[0]?.coach}</span>
                    <span style={matchupScore}>
                      {rec0.wins ?? 0}-{rec0.losses ?? 0} &nbsp;·&nbsp; {Number(pair[0]?.total_points || 0).toFixed(2)} pts
                    </span>
                  </div>
                  <span style={matchupVs}>vs</span>
                  <div style={matchupSide}>
                    {pair[1]?.isBye ? (
                      <span style={matchupByeTag}>BYE</span>
                    ) : (
                      <>
                        <MascotLogo
                          imageDataUrl={mascotFor(pair[1]?.coach)}
                          coachName={pair[1]?.coach}
                          size={144}
                          borderRadius="12px"
                          className="matchup-mascot-sm"
                          style={{ marginBottom: "0.45rem" }}
                        />
                        <span style={matchupCoachName}>{pair[1]?.coach}</span>
                        <span style={matchupScore}>
                          {rec1.wins ?? 0}-{rec1.losses ?? 0} &nbsp;·&nbsp; {Number(pair[1]?.total_points || 0).toFixed(2)} pts
                        </span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Week history ── */}
          {weekHistory.length > 0 && (
            <div style={historySection}>
              <h3 style={sectionTitle}>Completed Weeks</h3>
              {weekHistory.map((week) => (
                <div key={week.week} style={historyCard}>
                  <strong style={historyWeekLabel}>Week {week.week}</strong>
                  <div style={{ marginTop: "0.3rem", display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                    {(week.results || (week.winners || []).map((w) => ({ winner: w, coaches: [w, ""], pts: [null, null] }))).map((r, ri) => {
                      const [c0, c1] = r.coaches || [];
                      const [pts0, pts1] = r.pts || [null, null];
                      const loser = r.winner === c0 ? c1 : c0;
                      const winnerPts = r.winner === c0 ? pts0 : pts1;
                      const loserPts = r.winner === c0 ? pts1 : pts0;
                      const tb = r.tiebreak_pts;
                      const tbWinner = tb && r.winner ? (r.winner === c0 ? tb[0] : tb[1]) : null;
                      const tbLoser = tb && r.winner ? (r.winner === c0 ? tb[1] : tb[0]) : null;
                      const isMe = r.winner === myCoachName || loser === myCoachName || c0 === myCoachName || c1 === myCoachName;
                      const hasDetails = c1 && c1 !== "BYE" && pts0 != null && pts1 != null;
                      return (
                        <div
                          key={ri}
                          style={{
                            ...historyItem,
                            fontWeight: isMe ? 700 : 400,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <span>
                            {r.winner ? (
                              <>
                                <span style={{ color: r.winner === myCoachName ? "#a8fff0" : "#cddcf8" }}>{r.winner}</span>
                                {c1 && c1 !== "BYE" ? (
                                  <>
                                    {" def. "}<span style={{ color: loser === myCoachName ? "#ff93b0" : "#9fb7e3" }}>{loser}</span>
                                    {winnerPts != null && (
                                      <span style={{ color: "#6884b7" }}>
                                        {" "}({Number(winnerPts).toFixed(2)}–{Number(loserPts).toFixed(2)})
                                      </span>
                                    )}
                                    {tb && <span style={{ color: "#ffe6a6", fontSize: "0.72rem" }}> TB: {Number(tbWinner).toFixed(2)}–{Number(tbLoser).toFixed(2)}</span>}
                                  </>
                                ) : (
                                  <span style={{ color: "#6884b7" }}> (bye)</span>
                                )}
                              </>
                            ) : (
                              <>
                                <span>{c0}</span> tied <span>{c1}</span>
                                {pts0 != null && <span style={{ color: "#6884b7" }}> ({Number(pts0).toFixed(2)}–{Number(pts1).toFixed(2)})</span>}
                              </>
                            )}
                          </span>
                          {hasDetails && (
                            <button
                              type="button"
                              onClick={() => openMatchupDetails(week.week, ri)}
                              style={{
                                background: "transparent",
                                border: "1px solid #3d5680",
                                color: "#a8fff0",
                                borderRadius: "4px",
                                padding: "0.1rem 0.45rem",
                                fontSize: "0.68rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                letterSpacing: "0.03em",
                              }}
                            >
                              Match details
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {matchupDetails && (
        <MatchupDetailsModal
          state={matchupDetails}
          loading={detailsLoading}
          error={detailsError}
          onClose={closeMatchupDetails}
        />
      )}
    </div>
  );
};

export default Matchmaker;

// ── Styles ────────────────────────────────────────────────────────────────────

const page = { display: "flex", flexDirection: "column", gap: "1rem" };

const pageTitle = {
  fontSize: "1.15rem",
  fontWeight: 700,
  color: "#eaf3ff",
  margin: 0,
};

const subtitle = {
  fontSize: "0.82rem",
  color: "#9fb7e3",
  margin: 0,
  marginTop: "-0.5rem",
};

const stateText = { padding: "0.8rem", color: "#9fb7e3" };
const errorText = { padding: "0.8rem", color: "#ff93b0" };
const emptyHint = { color: "#9fb7e3", fontStyle: "italic" };

const myMatchupCard = {
  border: "2px solid #00f5d4",
  borderRadius: "14px",
  padding: "1.2rem 1.4rem",
  background: "rgba(0,245,212,0.07)",
  boxShadow: "0 0 18px rgba(0,245,212,0.12)",
  display: "flex",
  flexDirection: "column",
  gap: "0.7rem",
};

const myMatchupLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  color: "#00f5d4",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const myMatchupRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.25rem",
  flexWrap: "wrap",
};

const myMatchupCoach = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const myMatchupName = {
  fontSize: "1.75rem",
  fontWeight: 800,
  color: "#eaf3ff",
  letterSpacing: "0.01em",
};

const myMatchupOpponent = {
  fontSize: "1.75rem",
  fontWeight: 800,
  color: "#cddcf8",
  letterSpacing: "0.01em",
};

const myMatchupPts = {
  fontSize: "1rem",
  color: "#8ea8d8",
  marginTop: "0.15rem",
};

const vsText = {
  fontSize: "2.2rem",
  fontWeight: 800,
  color: "#9fb7e3",
  letterSpacing: "0.04em",
  padding: "0 0.35rem",
};

const byeText = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "#6884b7",
  fontStyle: "italic",
};

const sectionTitle = {
  fontSize: "0.88rem",
  fontWeight: 700,
  color: "#cddcf8",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  margin: 0,
};

const matchupList = {
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
};

const matchupCard = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0,
  padding: "0.65rem 0.9rem",
  borderRadius: "10px",
  background: "rgba(9,23,64,0.72)",
  border: "1px solid rgba(86,122,198,0.3)",
};

const matchupCardHighlight = {
  border: "1px solid rgba(0,245,212,0.45)",
  background: "rgba(0,245,212,0.05)",
};

const matchupSide = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
};

const matchupCoachName = {
  fontWeight: 700,
  color: "#eaf3ff",
  fontSize: "1.2rem",
  marginBottom: "0.1rem",
};

const matchupScore = {
  fontSize: "0.95rem",
  color: "#8ea8d8",
};

const matchupVs = {
  fontSize: "1.6rem",
  fontWeight: 800,
  color: "#9fb7e3",
  letterSpacing: "0.04em",
  padding: "0 0.35rem",
};

const matchupByeTag = {
  fontSize: "0.78rem",
  color: "#6884b7",
  fontStyle: "italic",
};

const historySection = {
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
};

const historyCard = {
  padding: "0.6rem 0.9rem",
  borderRadius: "10px",
  background: "rgba(9,23,64,0.55)",
  border: "1px solid rgba(86,122,198,0.2)",
};

const historyWeekLabel = {
  fontSize: "0.82rem",
  color: "#cddcf8",
};


const historyItem = {
  fontSize: "0.8rem",
  color: "#9fb7e3",
  lineHeight: "1.6",
};
